import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { API, BACKEND_URL } from '../utils/apiBase';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState({
    background_color: '#0f0f10',
    gradient_start: '#0f0f10',
    gradient_end: '#1a1625',
    primary_color: '#7c3aed',
    secondary_color: '#a855f7',
    accent_color: '#ec4899',
    text_color: '#ffffff',
    card_bg: 'rgba(255, 255, 255, 0.05)',
    button_gradient_start: '#7c3aed',
    button_gradient_end: '#a855f7',
    display_score_color: '#a78bfa',
    display_secondary_score: '#ec4899',
    judge_waiting_color: '#6b7280',
    judge_voted_color: '#eab308',
    judge_revealed_color: '#22c55e',
    display_title_gradient_start: '#a78bfa',
    display_title_gradient_end: '#ec4899',
    display_card_border: 'rgba(255, 255, 255, 0.1)',
    judge_status_text: '#9ca3af'
  });

  useEffect(() => {
    loadTheme();

    // Poll theme every 1500 ms — lighter on custom-domain edges
    // (Cloudflare Bot Fight Mode on judging.worldfreestylefootball.org
    // was throttling the 300 ms cadence and occasionally serving
    // "Access Denied" pages to tablets that shared an IP). Combined
    // with the refresh-on-focus/visibility/online listeners below,
    // theme changes still land on the Big Screen and Countdown display
    // within 1.5 s of the manager pushing them, which is fine for a
    // live event (the previous 300 ms was mostly wasted).
    const interval = setInterval(loadTheme, 1500);

    // Immediate refresh on tab visibility regain and network reconnect
    // so a manager coming back to the second screen sees the latest
    // config without waiting for the next poll.
    const onFocus = () => loadTheme();
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
    };
  }, []);

  const loadTheme = async () => {
    try {
      const res = await axios.get(`${API}/theme`);
      setTheme(res.data);
    } catch (error) {
      console.error('Error loading theme:', error);
    }
  };

  const updateTheme = async (updates) => {
    try {
      const res = await axios.put(`${API}/theme`, updates);
      setTheme(res.data);
      return true;
    } catch (error) {
      console.error('Error updating theme:', error);
      return false;
    }
  };

  const resetTheme = async () => {
    try {
      const res = await axios.post(`${API}/theme/reset`);
      setTheme(res.data);
      return true;
    } catch (error) {
      console.error('Error resetting theme:', error);
      return false;
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, updateTheme, resetTheme, loadTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
